import { nanoid } from "nanoid";
import Phaser from "phaser";
import { createRoot } from "react-dom/client";
import { UIContainer, UIScene } from "../ui/UITypes";

class Graphic {
  /**
   * Draw provided element to the game.
   * @param {object} scene - Game scene.
   * @param {number} position - position : {x , y}.
   * @param {number} size - size : {x , y}.
   * @param {object} element - Function JSX element to draw Ex. (props) => <Element {...props} />.
   * @param {boolean} interactable - Can this object interactable?.
   * @param {number} origin - default is 0.5 which is center.
   */
  public static Draw = (
    scene: UIScene,
    position: Phaser.Math.Vector2,
    size: Phaser.Math.Vector2,
    element: Function,
    tag: string = null,
    parent = null,
    interactable: boolean = true,
    origin: number = 0.5,
    callback: Function
  ) => {
    const id = nanoid();
    let div = document.createElement("div");
    div.id = id;
    div.style.width = size.x <= 0 ? "100%" : size.x + "px";
    div.style.height = size.y <= 0 ? "100%" : size.y + "px";

    let container: UIContainer = scene.add.container(position.x, position.y);
    let domElement = scene.add.dom(0, 0, div);
    domElement.pointerEvents = interactable ? "auto" : "none";
    domElement.setOrigin(origin);

    // domElement.createFromHTML();
    container.add([domElement]);

    if (parent) {
      document.getElementById(parent).children[0].appendChild(domElement.node);
    } else {
      // container.add([domElement]);
    }

    // let HTMLelement = document.createElement("div");
    let root = createRoot(domElement.node);
    root.render(element());
    if (tag !== null && !scene.tags.includes(tag)) {
      scene.tags.push(tag);
      scene.tags.sort();
    }
    container.tag = tag;
    container.root = root;
    container.domElement = domElement;
    container.update = (props) => {
      if (root) root.render(element(props));
    };
    if (callback) callback({ id, container });

    return { id, container };
  };

  /**
   * Update specific Graphic elements of the game.
   * @param {object} scene - Game scene.
   * @param {string} tagName - Tag name assigned when created with Graphic.Draw.
   * @param {object} conditions - Conditions for filtering target objects.
   */
  static GetByTagName = (
    scene: UIScene,
    tagName: string,
    conditions = null
  ) => {
    let list: UIContainer[] = scene.children.list
      .filter((gameObject) => gameObject.type === "Container")
      .filter((selected) => {
        const container = selected as UIContainer;
        return container.tag === tagName;
      }) as UIContainer[];

    list.forEach((gameObject) => {
      if (!conditions) return;

      for (const [key, value] of Object.entries(conditions)) {
        if (gameObject[key] !== value) {
          list = list.filter((value) => value !== gameObject);
        }
      }
    });

    return list;
  };

  /**
   * Check if specific Graphic is in the game.
   * @param {object} scene - Game scene.
   * @param {string} tagName - Tag name assigned when created with Graphic.Draw.
   */
  static isExisted = (scene: UIScene, tagName: string) => {
    let list = scene.children.list
      .filter((gameObject) => gameObject.type === "Container")
      .filter((selected) => {
        const container = selected as UIContainer;
        return container.tag === tagName;
      }) as UIContainer[];

    return list.length > 0 ? true : false;
  };

  /**
   * Update all Graphics element of the game.
   * @param {object} scene - Game scene.
   */
  static Update = (scene: UIScene) => {
    let list = scene.children.list.filter(
      (gameObject) => gameObject.type === "Container"
    );
    list.forEach((container: UIContainer) => {
      // console.log("Graphic Updated", container.tag);
      container.update();
    });

    return list;
  };

  /**
   * Update specific Graphic elements of the game.
   * @param {object} scene - Game scene.
   * @param {string} tagName - Tag name assigned when created with Graphic.Draw.
   * @param {any} conditions - Conditions for filtering target objects.
   */
  static UpdateByTagName = (
    scene: UIScene,
    tagName: string,
    conditions = null
  ) => {
    if (
      tagName.toLowerCase().includes("character") &&
      tagName.toLowerCase().includes("card")
    )
      console.warn(
        'Did you want to update a character card?, Try "UpdateCardByTagName" Instead'
      );

    let list = scene.children.list
      .filter((gameObject) => gameObject.type === "Container")
      .filter((selected) => {
        const container = selected as UIContainer;
        return container.tag === tagName;
      }) as UIContainer[];

    list.forEach((container) => {
      if (conditions) {
        for (const [key, value] of Object.entries(conditions)) {
          if (container[key] === value) {
            container.update();
          }
        }
      } else {
        console.log(tagName, "graphic updated");
        container.update();
      }
    });
  };

  /**
   * Delete a Graphic element of the game.
   * @param {object} scene - Game scene.
   * @param {object} gameObject - Tag name assigned when created with Graphic.Draw.
   */
  static Destroy = (scene: UIScene, gameObject: UIContainer) => {
    //Also delete tags
    if (Graphic.GetByTagName(scene, gameObject.tag).length <= 1)
      scene.tags = scene.tags.filter(
        (removedTag) => removedTag !== gameObject.tag
      );
    //Destroy object
    gameObject.root.unmount();
    gameObject.root = null;
    gameObject.destroy();
  };

  /**
   * Delete all Graphic element of the game.
   * @param {object} scene - Game scene.
   */
  static DestroyAll = (scene: UIScene) => {
    let list = scene.children.list.filter(
      (gameObject) => gameObject.type === "Container"
    ) as UIContainer[];
    list.forEach((container) => {
      //Also delete tags
      if (Graphic.GetByTagName(scene, container.tag).length <= 1)
        scene.tags = scene.tags.filter(
          (removedTag) => removedTag !== container.tag
        );
      //Destroy object
      container.root.unmount();
      container.root = null;
      container.destroy();
    });
  };

  /**
   * Delete specific Graphic elements of the game.
   * @param {object} scene - Game scene.
   * @param {string} tagName - Tag name assigned when created with Graphic.Draw.
   * @param {any} conditions - Conditions for filtering target objects.
   */
  static DestroyOnly = (scene: UIScene, tagName: string, conditions = null) => {
    let list = scene.children.list
      .filter((gameObject) => gameObject.type === "Container")
      .filter((selected) => {
        const container = selected as UIContainer;
        return container.tag === tagName;
      }) as UIContainer[];

    list.forEach((container) => {
      if (conditions) {
        for (const [key, value] of Object.entries(conditions)) {
          if (container[key] === value) {
            //Also delete tags
            if (Graphic.GetByTagName(scene, container.tag).length <= 1)
              scene.tags = scene.tags.filter(
                (removedTag) => removedTag !== container.tag
              );
            // RemoveFromDOM(container.list[0].node);
            container.root.unmount();
            container.root = null;
            container.destroy();
          }
        }
      } else {
        //Also delete tags
        if (Graphic.GetByTagName(scene, container.tag).length <= 1)
          scene.tags = scene.tags.filter(
            (removedTag) => removedTag !== container.tag
          );
        // RemoveFromDOM(container.list[0].node);
        container.root.unmount();
        container.root = null;
        container.destroy();
      }
    });
  };
}

export default Graphic;
